import { useRouter } from 'next/router';
import { useEffect } from 'react';

import CookieConsent, { useConsentCategory } from '@/components/consent/cookie-consent';
import {
  applyConsent,
  resolveClickEvent,
  trackEvent,
  trackPageView,
  warnIfLegacyGaId,
} from '@/lib/analytics';
import { initConsent } from '@/lib/consent';

/**
 * Consent-aware Google Analytics 4: loads gtag.js, sends page views on route
 * changes, forwards clicks on `data-track` elements and CTAs, and renders the
 * cookie banner and settings panel.
 *
 * What loads depends on the visitor's consent tier (`src/lib/consent.js`):
 * GA4 needs the analytics category, conversions and pixels the advertising
 * category. In strict regions both wait for an opt-in; elsewhere they apply
 * by notice. Changes take effect straight away, without a reload, and are
 * forwarded to Google Consent Mode. Withdrawing analytics also switches
 * gtag.js off and deletes its cookies and the journey trail (`applyConsent`
 * in `src/lib/analytics.js`). A Global Privacy Control signal keeps both
 * off. Nothing is rendered or loaded on the server.
 */
export default function Analytics() {
  const router = useRouter();
  const analytics = useConsentCategory('analytics');
  const ads = useConsentCategory('ads');

  useEffect(() => {
    warnIfLegacyGaId();
    void initConsent();
  }, []);

  useEffect(() => {
    // Loads gtag.js when analytics is allowed (Consent Mode defaults are set
    // before it is added), forwards changes to Consent Mode, and on a
    // withdrawal stops gtag.js and clears what analytics stored.
    applyConsent({ analytics, ads });
  }, [analytics, ads]);

  useEffect(() => {
    if (!analytics) return undefined;

    const handleClick = (event) => {
      const params = resolveClickEvent(event.target);
      if (params) trackEvent(params);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [analytics]);

  // A hash change is not a page view; `isReady` avoids a second view when a
  // static page picks up its query string after hydration.
  const route = router.isReady ? router.asPath.split('#')[0] : null;
  const { locale } = router;

  useEffect(() => {
    if (!analytics || route === null) return undefined;

    // `route` is the trigger; the path is read from the address bar so that it
    // includes the locale prefix. The timeout lets next/head apply the title.
    const timer = window.setTimeout(() => {
      trackPageView({ path: `${window.location.pathname}${window.location.search}`, locale });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [analytics, route, locale]);

  return <CookieConsent />;
}
